import React from 'react';

import { InView as ReactIntersectionObserver } from 'react-intersection-observer';

export interface IScrollAnimator<E extends HTMLElement> {
  delay: number;
  children?: RenderCallback<E>;
  render?: RenderCallback<E>;
  threshold?: number;
}

/**
 * If you want to add a new state:
 *  Add it to `initialState` and to `ScrollAnimatorComponentProps`
 *  Pass the new prop value in `renderProps` in the `render` method
 */

const initialState = {
  isInView: false,
};
type ComponentState = Readonly<typeof initialState>;

type RenderCallback<E extends HTMLElement> = (args: ScrollAnimatorComponentProps<E>) => React.ReactNode;
interface ScrollAnimatorComponentProps<E extends HTMLElement> {
  isInView: ComponentState['isInView'];
  ref: React.Ref<E>;
}

/**
 * Usage:
 *     <ScrollAnimator>
 *       {({ isInView }) => (
 *         <GridCell
 *           entry={item}
 *           className={cx(styles.gridCell, { 'in-view': isInView })}
 *         />
 *       )}
 *     </ScrollAnimator>
 */
export class ScrollAnimator<E extends HTMLElement = HTMLElement> extends React.Component<IScrollAnimator<E>, ComponentState> {
  static defaultProps = { delay: 0, threshold: .3 };

  readonly state: ComponentState = initialState;

  private timeoutId?: number;

  onEnter = () => {
    if (this.timeoutId !== undefined) return;

    if (this.props.delay > 0) {
      this.timeoutId = window.setTimeout(
        () => this.setState(prevState => ({ ...prevState, isInView: true })),
        this.props.delay
      );
      return;
    }

    this.setState(prevState => ({ ...prevState, isInView: true }));
  };

  onLeave = () => {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
    }

    if (!this.state.isInView) return;

    this.setState(prevState => ({ ...prevState, isInView: false }));
  };

  onChange = (inView: boolean) => {
    if (inView) {
      this.onEnter();
    } else {
      this.onLeave();
    }
  };

  render() {
    const { children, render, threshold } = this.props;

    const child = render || children;

    if (!child) return null;

    return(
      <ReactIntersectionObserver
        triggerOnce
        threshold={threshold}
        onChange={this.onChange}
      >
        {({ ref }) => typeof child === 'function' ? child({ isInView: this.state.isInView, ref }) : React.cloneElement(React.Children.only(child), { ref })}
      </ReactIntersectionObserver>
    );
  }
}
